import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, UserPlus } from "lucide-react";
import { usersApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { toast } from "sonner";

const ROLES = ["admin", "sales_manager", "sales_user"];

export default function UsersPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = () =>
    usersApi.list().then((r) => setUsers(r.data.items)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    try {
      await usersApi.update(u.id, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await usersApi.update(u.id, { active: !u.active });
      toast.success(u.active ? "User deactivated" : "User reactivated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Users</h1>
        <button data-testid="add-user-btn" onClick={() => setShowNew(true)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white hover:bg-teal-800">
          <Plus className="h-4 w-4" /> Add user
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="user-management-table">
        {loading ? (
          <div className="p-6 text-sm text-slate-400">Loading…</div>
        ) : (
          users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">
                  {u.name} {u.id === me?.id && <span className="text-xs font-normal text-slate-400">(you)</span>}
                </p>
                <p className="truncate text-xs text-slate-400">{u.email}</p>
              </div>
              <SelectInput
                testid={`user-role-${u.id}`}
                value={u.role}
                disabled={u.role === "super_admin" || u.id === me?.id}
                onChange={(e) => setRole(u, e.target.value)}
                className="!min-h-[40px] !w-auto px-2 text-sm"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                {u.role === "super_admin" && <option value="super_admin">super admin</option>}
              </SelectInput>
              <button
                data-testid={`user-toggle-${u.id}`}
                disabled={u.id === me?.id}
                onClick={() => toggleActive(u)}
                className={`min-h-[40px] rounded-lg border px-3 text-xs font-semibold disabled:opacity-40 ${
                  u.active ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
              >
                {u.active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          ))
        )}
      </div>

      <NewUserDialog open={showNew} onOpenChange={setShowNew} onCreated={load} />
    </div>
  );
}

function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales_user" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (open) { setForm({ name: "", email: "", password: "", role: "sales_user" }); setError(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await usersApi.create(form);
      toast.success("User created");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><UserPlus className="h-5 w-5" /> Add user</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><TextInput testid="new-user-name" value={form.name} onChange={set("name")} /></Field>
          <Field label="Email"><TextInput testid="new-user-email" type="email" value={form.email} onChange={set("email")} /></Field>
          <Field label="Temporary password"><TextInput testid="new-user-password" type="text" value={form.password} onChange={set("password")} placeholder="min 8 characters" /></Field>
          <Field label="Role">
            <SelectInput testid="new-user-role" value={form.role} onChange={set("role")}>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </SelectInput>
          </Field>
          {error && <p data-testid="new-user-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving} data-testid="new-user-submit" className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Creating…" : "Create user"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
